'use client';

import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useBannerAdOpen } from '@wt/game/providers/bannerAdOpen/BannerAdOpenProvider';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useMemo } from 'react';

import { guidelinesText } from './guidelinesText';
import { getSiteName } from '@wt/utilities/siteMode';

export const CommunityGuidelinesContent = ({
  showButtons,
  closeModal,
}: {
  showButtons?: boolean;
  closeModal?: () => void;
}) => {
  const router = useRouter();
  const { adOpen } = useBannerAdOpen();

  const top = useMemo(() => {
    return closeModal
      ? 'top-0'
      : adOpen
        ? 'top-[100px] md:top-[112px] lg:top-[164px]'
        : 'top-[54px] md:top-[78px]';
  }, [adOpen]);

  const bgColor = closeModal ? 'bg-base-200' : 'bg-base-100 md:bg-base-200';
  const marginAndPadding = closeModal ? 'mb-6' : 'md:my-6 md:pb-6 md:px-6';

  return (
    <div className="flex w-full flex-1 grow flex-col items-center px-4 md:px-8">
      <div
        className={`${marginAndPadding} flex max-w-4xl flex-col space-y-6 rounded-3xl text-sm md:bg-base-200`}
      >
        <div
          className={`sticky ${top} z-20 flex w-full flex-row items-center justify-between ${bgColor} py-6`}
        >
          {showButtons !== false && (
            <div onClick={() => (closeModal ? closeModal() : router.back())}>
              <ChevronLeftRoundedIcon className="m-0 fill-current text-2xl leading-none hover:cursor-pointer" />
            </div>
          )}
          <div className="mx-20 grow text-center">
            <span className="text-lg font-medium lg:text-4xl">
              Community Guidelines
            </span>
          </div>
          {showButtons !== false &&
            (!closeModal ? (
              <Link href={'/game'} className="hidden md:flex">
                <CloseRoundedIcon className="m-0 fill-current text-2xl leading-none" />
              </Link>
            ) : (
              <div onClick={closeModal}>
                <CloseRoundedIcon className="m-0 fill-current text-2xl leading-none hover:cursor-pointer" />
              </div>
            ))}
        </div>

        <p>
          Welcome to {getSiteName()}, a platform dedicated to creativity and
          community. To ensure a safe and enjoyable experience for all, we
          require all users to adhere to the following community guidelines. By
          participating in our community, you agree to these rules and our{' '}
          <Link href={'/privacy-policy'} className="underline">
            Privacy Policy
          </Link>
          .
        </p>

        <ol className="flex w-full flex-col gap-5">
          {guidelinesText.map((g, i) => {
            return (
              <div key={i}>
                <span className="text-lg font-bold">{`${i + 1}. ${g.title}`}</span>
                <ul className="mt-2 flex flex-col gap-3 md:ml-2">
                  {g.subSections.map((s, i) => {
                    return (
                      <p key={i}>
                        •<span className="ml-2 font-bold">{s.title}</span>:{' '}
                        {s.explanation}
                      </p>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </ol>

        <p>
          By following these guidelines, you help ensure that {getSiteName()}{' '}
          remains a creative, engaging, and safe environment for all users.
          Thank you for being a part of our community and for respecting these
          principles.
        </p>
      </div>
    </div>
  );
};
